import React, { useEffect, useMemo, useState } from 'react';
import { PeopleModule } from '../../Components/warehouse/PeopleModule';
import { printPersonCV } from '../../Components/print/printPersonCV';
import { PersonForm } from '../../Components/PersonForm/PersonForm';
import HrNoveltiesModule from './HrNoveltiesModule';
import RhIndicadoresModule from './RhIndicadoresModule';
import DotacionesModule from './DotacionesModule';
import SectionIndexCards from '../../Components/Common/SectionIndexCards';
import { Modal } from '../../Components/Modal/Modal';
import { Bell, BarChart3, CalendarDays, FileText, Shirt, UserPlus, Users } from 'lucide-react';
// Nota: En este panel usamos el objeto plano del formulario.
// No transformamos a FormData aquí; la validación de DNI se hace en useWarehouse.
import { api, setCurrentPageContext } from '../../services/api';
import { useUserPrefs } from '../../Components/context/UserPrefs';
import { DOCUMENT_FIELD_DEFS } from '../../utils/personFormAdapter';

const RECURSOS_HUMANOS_SECTION_ORDER = ['indice', 'activos', 'cv', 'vacaciones', 'novedades', 'indicadores', 'dotaciones'];

function daysBetweenInclusive(startStr, endStr) {
  if (!startStr || !endStr) return null;
  const a = new Date(`${startStr}T00:00:00`);
  const b = new Date(`${endStr}T00:00:00`);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime()) || b < a) return null;
  return Math.round((b - a) / 86400000) + 1;
}

// Modal "Registrar vacación": crea (o edita, si viene record) un evento de
// vacaciones para un colaborador puntual. Cada registro queda como una
// fila nueva en hr_vacations — no sobrescribe los anteriores, así se
// arma el histórico real que antes no existía (localStorage tenía una
// sola fila por persona).
function VacationFormModal({ person, record, onClose, onSaved }) {
  const isEdit = !!record?.id;
  const [form, setForm] = useState({
    periodLabel: record?.period_label || '',
    vacationStart: record?.vacation_start_date ? String(record.vacation_start_date).slice(0, 10) : '',
    vacationEnd: record?.vacation_end_date ? String(record.vacation_end_date).slice(0, 10) : '',
    daysTaken: record?.days_taken ?? '',
    notes: record?.notes || '',
  });
  const [daysManuallyEdited, setDaysManuallyEdited] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleDateChange = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (!daysManuallyEdited) {
        const auto = daysBetweenInclusive(next.vacationStart, next.vacationEnd);
        if (auto !== null) next.daysTaken = auto;
      }
      return next;
    });
  };

  const submit = async () => {
    setError('');
    if (!form.vacationStart) {
      setError('La fecha de inicio de período de vacaciones es obligatoria.');
      return;
    }
    if (form.vacationEnd && form.vacationEnd < form.vacationStart) {
      setError('La fecha de fin no puede ser anterior a la de inicio.');
      return;
    }
    setSaving(true);
    try {
      const res = await api.hr.vacations.upsert({
        ...(isEdit ? { id: record.id } : {}),
        person_id: person?._dbId || null,
        dni: person?.id || '',
        collaborator_name: person?.name || '',
        period_label: form.periodLabel.trim(),
        vacation_start_date: form.vacationStart,
        vacation_end_date: form.vacationEnd || null,
        days_taken: form.daysTaken === '' ? 0 : Number(form.daysTaken),
        notes: form.notes.trim(),
      });
      if (!res?.ok) throw new Error(res?.error || 'No fue posible guardar el registro.');
      await onSaved();
    } catch (e) {
      setError(e?.message || 'No fue posible guardar el registro.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={`${isEdit ? 'Editar' : 'Registrar'} vacación — ${person?.name || ''}`} size="max-w-lg">
      <div className="space-y-3">
        <div>
          <label className="block text-xs text-gray-600 mb-1">Período que se toma</label>
          <input
            value={form.periodLabel}
            onChange={(e) => setForm((p) => ({ ...p, periodLabel: e.target.value }))}
            placeholder="Ej: 2024-2025, Período 2"
            className="w-full border rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Fecha inicio de período de vacaciones *</label>
            <input
              type="date"
              value={form.vacationStart}
              onChange={(e) => handleDateChange('vacationStart', e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Fecha fin de vacaciones</label>
            <input
              type="date"
              value={form.vacationEnd}
              onChange={(e) => handleDateChange('vacationEnd', e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Días tomados</label>
          <input
            type="number"
            min="0"
            step="1"
            value={form.daysTaken}
            onChange={(e) => { setDaysManuallyEdited(true); setForm((p) => ({ ...p, daysTaken: e.target.value })); }}
            className="w-32 border rounded-lg px-3 py-2 text-sm"
          />
          <p className="text-xs text-gray-500 mt-1">Se calcula solo a partir de las fechas si no lo cambias a mano.</p>
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Observaciones (opcional)</label>
          <textarea
            value={form.notes}
            onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
            rows={2}
            className="w-full border rounded-lg px-3 py-2 text-sm"
          />
        </div>
        {error && <div className="px-3 py-2 rounded-lg border text-sm bg-red-50 border-red-200 text-red-700">{error}</div>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border text-sm">Cancelar</button>
          <button
            type="button"
            disabled={saving}
            onClick={submit}
            className="px-4 py-2 rounded-lg text-white text-sm disabled:opacity-60"
            style={{ backgroundColor: '#16A34A' }}
          >
            {saving ? 'Guardando...' : isEdit ? 'Guardar cambios' : 'Registrar'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// Modal "Ver historial": consulta de solo lectura de todas las vacaciones
// tomadas por un colaborador — permite saber cuándo se tomaron antes y
// corregir/borrar un registro puntual si se cargó mal.
function VacationHistoryModal({ person, records, onClose, onEdit, onDeleted }) {
  const [deletingId, setDeletingId] = useState(null);

  const handleDelete = async (record) => {
    if (!window.confirm('¿Eliminar este registro de vacaciones?')) return;
    setDeletingId(record.id);
    try {
      await api.hr.vacations.delete(record.id);
      await onDeleted();
    } catch (e) {
      window.alert(e?.message || 'No fue posible eliminar el registro.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={`Historial de vacaciones — ${person?.name || ''}`} size="max-w-2xl">
      <div className="space-y-3">
        {(!records || records.length === 0) ? (
          <div className="text-sm text-gray-600 py-6 text-center">Este colaborador no tiene vacaciones registradas.</div>
        ) : (
          <div className="border rounded-lg overflow-hidden overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="px-3 py-2 text-left">Período</th>
                  <th className="px-3 py-2 text-left">Fecha inicio</th>
                  <th className="px-3 py-2 text-left">Fecha fin</th>
                  <th className="px-3 py-2 text-right">Días</th>
                  <th className="px-3 py-2 text-left">Registrado por</th>
                  <th className="px-3 py-2 text-left">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {records.map((r) => (
                  <tr key={r.id} className="border-t">
                    <td className="px-3 py-2">{r.period_label || '-'}</td>
                    <td className="px-3 py-2 font-mono text-xs">{String(r.vacation_start_date || '').slice(0, 10) || '-'}</td>
                    <td className="px-3 py-2 font-mono text-xs">{String(r.vacation_end_date || '').slice(0, 10) || '-'}</td>
                    <td className="px-3 py-2 text-right">{r.days_taken ?? 0}</td>
                    <td className="px-3 py-2 text-xs text-gray-600">{r.created_by_name || '-'}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={() => onEdit(r)} className="px-2 py-1 rounded border bg-white hover:bg-gray-50 text-xs">Editar</button>
                        <button
                          type="button"
                          disabled={deletingId === r.id}
                          onClick={() => handleDelete(r)}
                          className="px-2 py-1 rounded border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 text-xs disabled:opacity-60"
                        >
                          {deletingId === r.id ? '...' : 'Eliminar'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex justify-end">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border text-sm">Cerrar</button>
        </div>
      </div>
    </Modal>
  );
}

export default function RecursosHumanosPanel({ location, showLocation = true, people, onAddPerson, onUpdatePerson, onDeletePerson }) {
  const [tab, setTab] = useState('activos'); // 'indice' | 'activos' | 'cv' | 'vacaciones' | 'novedades'
  // Reporta a Administración TI > Auditoría qué submódulo de Recursos
  // Humanos está viendo el usuario (más específico que el módulo que ya
  // reporta Sede.jsx), vía la cabecera X-Senerfi-Page (ver src/services/api.js).
  useEffect(() => {
    setCurrentPageContext(`/sede/${location || ''}/recursos-humanos/${tab || ''}`);
  }, [location, tab]);
  const { currentUser, getSectionPrefs } = useUserPrefs();
  const sectionPrefs = useMemo(() => getSectionPrefs(currentUser?.id), [getSectionPrefs, currentUser]);
  // Si el permiso de una sección no está definido para el usuario (nunca se
  // tocó su casilla en Administración TI), se sigue mostrando por defecto
  // (compatibilidad con cuentas existentes); solo se oculta si quedó
  // explícitamente desmarcada (=== false).
  const allowedSections = useMemo(() => sectionPrefs?.['recursos-humanos'] || {}, [sectionPrefs]);
  const isSectionAllowed = (key) => allowedSections[key] !== false;

  // Si al usuario le desmarcan la sección en la que estaba parado (o entra
  // directo a una que no tiene permitida), lo movemos a la primera que sí
  // pueda ver — evita quedar viendo contenido de una sección oculta.
  useEffect(() => {
    if (isSectionAllowed(tab)) return;
    const firstAllowed = RECURSOS_HUMANOS_SECTION_ORDER.find((k) => isSectionAllowed(k));
    if (firstAllowed) setTab(firstAllowed);
  }, [tab, allowedSections]); // eslint-disable-line react-hooks/exhaustive-deps
  const [selectedDoc, setSelectedDoc] = useState('');
  const [createKey, setCreateKey] = useState(0);
  const [createStatus, setCreateStatus] = useState(null);
  const [operationalRoleCatalog, setOperationalRoleCatalog] = useState([]);
  const [cvOperationalRole, setCvOperationalRole] = useState('');
  const [cvRoleSaving, setCvRoleSaving] = useState(false);
  const [overridePeople, setOverridePeople] = useState([]);
  const [peopleReloadKey, setPeopleReloadKey] = useState(0);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyError, setHistoryError] = useState('');
  const [historySelectedId, setHistorySelectedId] = useState(null);
  const displayNames = {
    'popayan': 'Popayán',
    'santander-de-quilichao': 'Santander de Quilichao',
  };
  const title = displayNames[location] || location;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.people.operationalRoleCatalog();
        const rows = Array.isArray(res?.data) ? res.data : (Array.isArray(res?.data?.data) ? res.data.data : []);
        if (!cancelled) setOperationalRoleCatalog(rows.filter(Boolean));
      } catch (_) {
        if (!cancelled) setOperationalRoleCatalog([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const formatCargoRolLine = (p) => {
    const cargo = p?.position || '-';
    const rol = p?.operationalRole;
    if (!rol) return cargo;
    return `${cargo} · Rol operativo: ${rol}`;
  };

  // Fallback: si no llegan personas desde el hook, cargar directamente desde la API y fusionar con RRHH
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const base = Array.isArray(people) ? people : [];
      try {
        const pRes = await api.people.get({ limit: 1000, clean: 1 });
        // Soportar ambos formatos: lista { data: [...] } y único { data: {...} }
        let persons = Array.isArray(pRes?.data?.data)
          ? pRes.data.data
          : (Array.isArray(pRes?.data) ? pRes.data : (pRes?.data ? [pRes.data] : []));
        let merged = persons;
        try {
          const heRes = await api.hr.employees.list({ limit: 1000 });
          const heRows = Array.isArray(heRes?.data) ? heRes.data : [];
          // Si no hay personas pero sí registros de RRHH, crear base desde RRHH
          if ((!Array.isArray(persons) || persons.length === 0) && heRows.length > 0) {
            persons = heRows.map((r) => ({
              id: r?.dni || String(r?.person_id || ''),
              name: r?.name || '',
              phone: r?.phone || '',
              email: r?.email || '',
              position: r?.position || '',
              operationalRole: r?.operational_role || '',
              area: r?.department || '',
              status: r?.status || 'Activo',
            })).filter(p => p.id);
            merged = persons;
          }
          const byDni = new Map(heRows.filter(r => r?.dni).map(r => [String(r.dni), r]));
          const byId = new Map(heRows.filter(r => r?.person_id).map(r => [String(r.person_id), r]));
          merged = persons.map(p => {
            const hr = byDni.get(String(p.dni || p.id)) || (p._dbId ? byId.get(String(p._dbId)) : undefined) || byId.get(String(p.id));
            if (!hr) return p;
            return {
              ...p,
              position: hr.position || p.position,
              operationalRole: p.operationalRole || hr.operational_role || p.operational_role,
              area: hr.department || p.area,
              hireDate: hr.hire_date || p.hireDate,
              contractType: hr.contract_type || p.contractType,
              currentContractModality: p.currentContractModality || hr.contract_type || p.currentContractModality,
              status: hr.status || p.status,
            };
          });
        } catch (_) {
          // si falla RRHH, usar solo people
        }
        if (!cancelled) {
          const baseLen = Array.isArray(base) ? base.length : 0;
          const mergedLen = Array.isArray(merged) ? merged.length : 0;
          if (mergedLen > baseLen) setOverridePeople(merged);
          else setOverridePeople([]);
        }
      } catch (err) {
        // No bloquear la UI; mantener override vacío si falla
        if (!cancelled) setOverridePeople([]);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [people, peopleReloadKey]);

  const peopleSource = useMemo(() => {
    const local = Array.isArray(overridePeople) ? overridePeople : [];
    return (local.length > 0) ? local : (Array.isArray(people) ? people : []);
  }, [overridePeople, people]);

  const looksLikeVehiclePlate = (p) => {
    const token = String(p?.dni || p?.id || '')
      .replace(/[\s.\-]+/g, '')
      .toUpperCase();
    if (!token || /^\d{6,12}$/.test(token)) return false;
    return /^[A-Z]{3}\d{3}$/.test(token)
      || /^[A-Z]{3}\d{2}[A-Z0-9]$/.test(token)
      || /^[A-Z]{2,3}\d{2,3}[A-Z0-9]?$/.test(token);
  };

  const activePeople = useMemo(
    () => (peopleSource || []).filter(p => {
      if (looksLikeVehiclePlate(p)) return false;
      // Normalizar posibles espacios/variantes en el estado
      const s = String(p.status || p.estado || '').trim().toLowerCase();
      // Si no hay estado definido, considerarlo activo para no ocultar registros existentes
      return s ? (s === 'activo') : true;
    }),
    [peopleSource]
  );

  const selectedPerson = useMemo(
    () => (peopleSource || []).find(p => String(p.id) === String(selectedDoc)),
    [peopleSource, selectedDoc]
  );

  useEffect(() => {
    setCvOperationalRole(selectedPerson?.operationalRole || '');
  }, [selectedPerson]);

  const saveCvOperationalRole = async () => {
    if (!selectedPerson) return;
    const personId = selectedPerson._dbId;
    const dni = selectedPerson.dni || selectedPerson.id;
    const name = selectedPerson.name || '';
    if (!personId && !dni) return;
    setCvRoleSaving(true);
    try {
      await api.people.upsert({
        ...(personId ? { id: personId } : {}),
        dni,
        name,
        operationalRole: cvOperationalRole || '',
      });
      setPeopleReloadKey((k) => k + 1);
    } catch (e) {
      window.alert(e?.message || 'No fue posible guardar el rol operativo.');
    } finally {
      setCvRoleSaving(false);
    }
  };

  // Certificados/adjuntos disponibles para la hoja de vida del colaborador
  // seleccionado: une los adjuntos genéricos (legacy) con los documentos
  // tipados (Cédula, certificados de alturas, EMO, etc.) que sí tengan
  // archivo cargado.
  const availableCertificates = useMemo(() => {
    if (!selectedPerson) return [];
    const fromAttachments = (Array.isArray(selectedPerson.attachments) ? selectedPerson.attachments : [])
      .filter((a) => a && a.url)
      .map((a, idx) => ({ key: `att:${idx}`, label: a.name || `Adjunto ${idx + 1}`, url: a.url }));
    const docsMap = (selectedPerson.documents && typeof selectedPerson.documents === 'object') ? selectedPerson.documents : {};
    const fromDocuments = DOCUMENT_FIELD_DEFS
      .filter((d) => docsMap[d.key])
      .map((d) => ({ key: `doc:${d.key}`, label: d.label, url: docsMap[d.key] }));
    return [...fromAttachments, ...fromDocuments];
  }, [selectedPerson]);

  const [selectedCertKeys, setSelectedCertKeys] = useState(() => new Set());

  useEffect(() => {
    setSelectedCertKeys(new Set(availableCertificates.map((c) => c.key)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDoc]);

  const toggleCertKey = (key) => {
    setSelectedCertKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  useEffect(() => {
    setHistoryOpen(false);
    setHistoryLoading(false);
    setHistoryRows([]);
    setHistoryError('');
    setHistorySelectedId(null);
  }, [selectedDoc]);

  const parseHistoryResponse = (r) => {
    if (!r) return [];
    const d = r?.data;
    if (Array.isArray(d?.data)) return d.data;
    if (Array.isArray(d)) return d;
    return [];
  };

  const openHistory = async () => {
    if (!selectedPerson) return;
    setHistoryOpen(true);
    setHistoryLoading(true);
    setHistoryError('');
    setHistoryRows([]);
    setHistorySelectedId(null);
    try {
      const res = await api.people.employmentHistory({ dni: selectedPerson.id, limit: 100, order: 'desc' });
      const rows = parseHistoryResponse(res);
      setHistoryRows(Array.isArray(rows) ? rows : []);
      const firstId = (Array.isArray(rows) && rows[0] && rows[0].id) ? rows[0].id : null;
      setHistorySelectedId(firstId);
    } catch (e) {
      setHistoryError(e?.message || 'No fue posible cargar el historial.');
    } finally {
      setHistoryLoading(false);
    }
  };

  // Control de Vacaciones: historial real en el backend (hr_vacations) —
  // reemplaza el localStorage que había antes (una sola fila por
  // colaborador que se sobrescribía, sin historial ni respaldo en base de
  // datos). Cada fila de vacationRecords es un evento (una toma de
  // vacaciones puntual); un colaborador puede tener varias a lo largo del
  // tiempo.
  const [vacationRecords, setVacationRecords] = useState([]);
  const [vacationsLoading, setVacationsLoading] = useState(false);
  const [vacationFormTarget, setVacationFormTarget] = useState(null); // persona (para registrar) o { person, record } (para editar)
  const [vacationHistoryTarget, setVacationHistoryTarget] = useState(null); // persona (para ver historial)
  const [vacationsQuery, setVacationsQuery] = useState('');

  const loadVacationRecords = async () => {
    setVacationsLoading(true);
    try {
      const res = await api.hr.vacations.list({});
      const rows = Array.isArray(res?.data) ? res.data : [];
      setVacationRecords(rows);
    } catch (_) {
      setVacationRecords([]);
    } finally {
      setVacationsLoading(false);
    }
  };

  useEffect(() => {
    if (tab === 'vacaciones') loadVacationRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const vacationRecordsByPerson = useMemo(() => {
    const map = new Map();
    (vacationRecords || []).forEach((r) => {
      const key = String(r.dni || r.person_id || '').trim();
      if (!key) return;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    });
    return map;
  }, [vacationRecords]);

  const getVacationRecordsFor = (person) => {
    const byDni = vacationRecordsByPerson.get(String(person?.id || '').trim()) || [];
    const byPersonId = person?._dbId ? (vacationRecordsByPerson.get(String(person._dbId)) || []) : [];
    if (byDni.length) return byDni;
    return byPersonId;
  };

  const filteredVacationPeople = useMemo(() => {
    const q = vacationsQuery.trim().toLowerCase();
    if (!q) return activePeople || [];
    return (activePeople || []).filter((p) => {
      const hay = `${p.name || ''} ${p.id || ''} ${p.position || ''} ${p.operationalRole || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [activePeople, vacationsQuery]);


  const parseBirthDate = (value) => {
    if (!value) return null;
    let d = new Date(value);
    if (!isNaN(d)) return d;
    const m1 = String(value).match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{2,4})$/);
    if (m1) {
      const day = parseInt(m1[1], 10);
      const month = parseInt(m1[2], 10) - 1;
      const year = parseInt(m1[3], 10);
      d = new Date(year, month, day);
      if (!isNaN(d)) return d;
    }
    const m2 = String(value).toLowerCase().match(/^(\d{1,2})\s+de\s+([a-zñ]+)(?:\s+de\s+(\d{2,4}))?$/i);
    if (m2) {
      const months = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre'];
      const day = parseInt(m2[1], 10);
      const monthIdx = months.indexOf(m2[2]);
      const year = m2[3] ? parseInt(m2[3], 10) : new Date().getFullYear();
      if (monthIdx >= 0) {
        d = new Date(year, monthIdx, day);
        if (!isNaN(d)) return d;
      }
    }
    return null;
  };

  const computeAge = (value) => {
    const d = parseBirthDate(value);
    if (!d) return '';
    const now = new Date();
    let age = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
    return age >= 0 ? age : '';
  };

  const daysTo = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (isNaN(d)) return null;
    const a = new Date(); a.setHours(0,0,0,0);
    const b = new Date(d); b.setHours(0,0,0,0);
    return Math.round((b - a) / 86400000);
  };

  // Utilidades para Control-Vacaciones
  const parseYMD = (v) => {
    if (!v) return null;
    const d = new Date(v);
    return isNaN(d) ? null : d;
  };
  const computeAccruedDays = (joinStr) => {
    const j = parseYMD(joinStr);
    if (!j) return 0;
    const a = new Date(j.getFullYear(), j.getMonth(), j.getDate());
    const b = new Date(); b.setHours(0,0,0,0);
    const days = Math.max(0, Math.round((b - a) / 86400000));
    const accrued = (days * 15) / 365; // 15 días por año
    return Math.max(0, Math.floor(accrued));
  };

  return (
    <div className="text-gray-800 space-y-6">
      <div>
        <h2 className="text-2xl font-semibold mb-1">Recursos Humanos</h2>
        {showLocation && (
          <p className="text-gray-700">Sede: {title}</p>
        )}
      </div>

      <section className="bg-white rounded-xl shadow border border-gray-200 p-5">
        <SectionIndexCards
          title="Indice de secciones"
          helperText="Accede rapido a los procesos principales de Recursos Humanos."
          columnsClassName="md:grid-cols-2 xl:grid-cols-3"
          className="mb-5 p-0 shadow-none border-0"
          items={[
            {
              key: 'indice',
              title: 'Nuevo colaborador',
              description: 'Registro y vinculacion de personal',
              icon: UserPlus,
              gradient: 'linear-gradient(135deg, #2563EB 0%, #4F46E5 100%)',
              accentGradient: 'linear-gradient(180deg, #3B82F6 0%, #4F46E5 100%)',
              active: tab === 'indice',
              onClick: () => setTab('indice'),
            },
            {
              key: 'activos',
              title: 'Colaboradores activos',
              description: 'Consulta y gestion del personal activo',
              icon: Users,
              gradient: 'linear-gradient(135deg, #0F766E 0%, #14B8A6 100%)',
              accentGradient: 'linear-gradient(180deg, #14B8A6 0%, #0F766E 100%)',
              active: tab === 'activos',
              onClick: () => setTab('activos'),
            },
            {
              key: 'cv',
              title: 'Hoja de vida',
              description: 'Generacion e impresion del expediente',
              icon: FileText,
              gradient: 'linear-gradient(135deg, #EA580C 0%, #F59E0B 100%)',
              accentGradient: 'linear-gradient(180deg, #F97316 0%, #EA580C 100%)',
              active: tab === 'cv',
              onClick: () => setTab('cv'),
            },
            {
              key: 'vacaciones',
              title: 'Control vacaciones',
              description: 'Seguimiento de fechas y periodos',
              icon: CalendarDays,
              gradient: 'linear-gradient(135deg, #16A34A 0%, #84CC16 100%)',
              accentGradient: 'linear-gradient(180deg, #84CC16 0%, #16A34A 100%)',
              active: tab === 'vacaciones',
              onClick: () => setTab('vacaciones'),
            },
            {
              key: 'novedades',
              title: 'Novedades',
              description: 'Registro y control de eventos del personal',
              icon: Bell,
              gradient: 'linear-gradient(135deg, #7C3AED 0%, #A855F7 100%)',
              accentGradient: 'linear-gradient(180deg, #A855F7 0%, #7C3AED 100%)',
              active: tab === 'novedades',
              onClick: () => setTab('novedades'),
            },
            {
              key: 'indicadores',
              title: 'Indicadores del área',
              description: 'Permisos, tipos de novedad y ausentismo por mes',
              icon: BarChart3,
              gradient: 'linear-gradient(135deg, #0369A1 0%, #0EA5E9 100%)',
              accentGradient: 'linear-gradient(180deg, #0EA5E9 0%, #0369A1 100%)',
              active: tab === 'indicadores',
              onClick: () => setTab('indicadores'),
            },
            {
              key: 'dotaciones',
              title: 'Control de dotaciones',
              description: 'Entregas y próximo ciclo cada 4 meses',
              icon: Shirt,
              gradient: 'linear-gradient(135deg, #0F766E 0%, #22C55E 100%)',
              accentGradient: 'linear-gradient(180deg, #22C55E 0%, #0F766E 100%)',
              active: tab === 'dotaciones',
              onClick: () => setTab('dotaciones'),
            },
          ].filter((item) => isSectionAllowed(item.key))}
        />

        {tab === 'indice' && (
          <div className="space-y-5">
            <section className="bg-gray-50 rounded-xl border p-4">
              <h4 className="text-base font-semibold mb-3">Registrar nuevo colaborador</h4>
              <p className="text-xs text-gray-600 mb-3">
                Al guardar, el formulario se reinicia para registrar otro colaborador.
              </p>
              {createStatus && (
                <div className={`mb-3 p-2 rounded text-sm ${
                  createStatus.type === 'ok'
                  ? 'bg-green-50 text-green-700 border border-green-200'
                  : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {createStatus.text}
                  </div>
                )}
                  <div className="bg-white rounded-lg border p-4 space-y-4">
                    <PersonForm
                    key={createKey}
                     person={null}
                     showExtraHRFields={true}
                     activePeople={activePeople}
                     onSave={async (data) => {
                      try {
                        const res = await onAddPerson(data);
                        if (res && res.success === false) {
                          setCreateStatus({ type: 'error', text: res.message || 'No se pudo crear el colaborador.' });
                        } else {
                          const up = res?.uploads || {};
                          const photoMsg = up.photo?.saved_count ? `Foto: ${up.photo.saved_count}` : '';
                          const attMsg = up.attachments?.saved_count ? `Adjuntos: ${up.attachments.saved_count}` : '';
                          const sigMsg = up.signature?.saved_count ? `Firma: ${up.signature.saved_count}` : '';
                          const extras = [photoMsg, attMsg, sigMsg].filter(Boolean).join(' · ');
                          setCreateStatus({ type: 'ok', text: extras ? `Colaborador creado. ${extras}` : 'Colaborador creado exitosamente.' });
                          setCreateKey((k) => k + 1);
                        }
                      } catch (error) {
                        setCreateStatus({ type: 'error', text: error.message || 'No se pudo crear el colaborador.' });
                      }
                    }}
                    onCancel={() => {
                    setCreateStatus(null);
                    setCreateKey((k) => k + 1);
                    }}
                    />
                </div>    
            </section>
          </div>
        )}

        {tab === 'activos' && (
          <PeopleModule
            people={peopleSource}
            onAdd={onAddPerson}
            onUpdate={onUpdatePerson}
            onDelete={onDeletePerson}
            showHeader={false}
            showExtraHRFields={true}
            showCreateButton={false}
            onlyCollaboratorFields={true}
            showActivateButton={true}
            onActivated={() => setPeopleReloadKey((k) => k + 1)}
          />
        )}

        {tab === 'cv' && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium mb-1">Selecciona colaborador</label>
                <select
                  value={selectedDoc}
                  onChange={(e) => setSelectedDoc(e.target.value)}
                  className="border rounded px-3 py-2 text-sm w-full"
                >
                  <option value="">Seleccione...</option>
                  {(peopleSource || []).map((p) => (
                    <option key={p.id} value={p.id}>{p.name} ({p.id})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">&nbsp;</label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={!selectedPerson}
                    onClick={() => {
                      if (!selectedPerson) return;
                      const chosen = availableCertificates
                        .filter((c) => selectedCertKeys.has(c.key))
                        .map((c) => ({ name: c.label, url: c.url }));
                      printPersonCV(selectedPerson, chosen);
                    }}
                    className={`px-3 py-2 rounded-md text-sm border ${selectedPerson ? 'bg-gray-800 text-white border-gray-800' : 'bg-gray-200 text-gray-500 border-gray-300 cursor-not-allowed'}`}
                  >
                    Imprimir hoja de vida
                  </button>
                  <button
                    type="button"
                    disabled={!selectedPerson}
                    onClick={openHistory}
                    className={`px-3 py-2 rounded-md text-sm border ${selectedPerson ? 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50' : 'bg-gray-200 text-gray-500 border-gray-300 cursor-not-allowed'}`}
                  >
                    Historial del colaborador
                  </button>
                </div>
              </div>
            </div>

            {historyOpen && (
              <div className="bg-white border rounded-lg p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-sm font-semibold text-gray-800">Historial del colaborador</div>
                  <button type="button" className="text-sm text-gray-600 hover:text-gray-800" onClick={() => setHistoryOpen(false)}>
                    Cerrar
                  </button>
                </div>
                {historyLoading ? (
                  <div className="text-sm text-gray-600 mt-3">Cargando...</div>
                ) : historyError ? (
                  <div className="text-sm text-red-600 mt-3">{historyError}</div>
                ) : (
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="md:col-span-1 border rounded-lg overflow-hidden">
                      <div className="bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-700">Registros</div>
                      <div className="max-h-[320px] overflow-auto divide-y">
                        {(Array.isArray(historyRows) ? historyRows : []).length === 0 ? (
                          <div className="px-3 py-3 text-sm text-gray-600">Sin historial.</div>
                        ) : (
                          (historyRows || []).map((r) => {
                            const active = String(historySelectedId) === String(r?.id);
                            const dt = r?.created_at || '';
                            const label = r?.event === 'reactivation' ? 'Reactivación' : (r?.event || 'Registro');
                            const sub = `${r?.old_status || ''}${(r?.old_status && r?.new_status) ? ' → ' : ''}${r?.new_status || ''}`.trim();
                            return (
                              <button
                                key={r?.id}
                                type="button"
                                onClick={() => setHistorySelectedId(r?.id)}
                                className={`w-full text-left px-3 py-2 ${active ? 'bg-blue-50' : 'bg-white hover:bg-gray-50'}`}
                              >
                                <div className="text-sm font-medium text-gray-800">{label}</div>
                                <div className="text-xs text-gray-600">{dt}</div>
                                {sub ? <div className="text-xs text-gray-500">{sub}</div> : null}
                              </button>
                            );
                          })
                        )}
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      {(() => {
                        const sel = (historyRows || []).find((x) => String(x?.id) === String(historySelectedId)) || null;
                        const snap = sel?.snapshot && typeof sel.snapshot === 'object' ? sel.snapshot : null;
                        if (!snap) {
                          return <div className="text-sm text-gray-600">Selecciona un registro para ver el detalle.</div>;
                        }
                        const p = snap;
                        return (
                          <div className="border rounded-lg p-4 text-gray-800">
                            <div className="flex flex-col sm:flex-row gap-4 items-start">
                              <div className="w-24 h-24 border rounded overflow-hidden bg-gray-50 flex items-center justify-center">
                                {p.photo ? (
                                  <img src={p.photo} alt="Foto de perfil" className="w-full h-full object-cover" />
                                ) : (
                                  <span className="text-xs text-gray-400">Sin foto</span>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="text-lg font-semibold truncate">{p.name || '-'}</h3>
                                <p className="text-gray-600">{formatCargoRolLine(p)}{p.area ? ` · ${p.area}` : ''}</p>
                                <p className="text-sm text-gray-500">Documento: {p.id || p.dni || '-'}</p>
                              </div>
                              <div className="text-sm">
                                <p><span className="font-medium">Estado:</span> {p.status || '-'}</p>
                                <p><span className="font-medium">Validación Cliente:</span> {p.clientValidation || '-'}</p>
                              </div>
                            </div>

                            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                              <section className="space-y-1">
                                <h4 className="text-sm font-semibold text-gray-800">Datos personales</h4>
                                <div className="grid grid-cols-2 gap-x-4 text-sm">
                                  <span className="text-gray-500">Fecha de nacimiento</span>
                                  <span>{p.birthDate || '-'}</span>
                                  <span className="text-gray-500">Años</span>
                                  <span>{computeAge(p.birthDate) || '-'}</span>
                                  <span className="text-gray-500">Tipo de sangre</span>
                                  <span>{p.bloodType || '-'}</span>
                                  <span className="text-gray-500">Dirección</span>
                                  <span>{p.address || '-'}</span>
                                  <span className="text-gray-500">Fecha de ingreso</span>
                                  <span>{p.hireDate || '-'}</span>
                                </div>
                              </section>

                              <section className="space-y-1">
                                <h4 className="text-sm font-semibold text-gray-800">Contacto</h4>
                                <div className="grid grid-cols-2 gap-x-4 text-sm">
                                  <span className="text-gray-500">Teléfono</span>
                                  <span>{p.phone || '-'}</span>
                                  <span className="text-gray-500">Email</span>
                                  <span>{p.email || '-'}</span>
                                  <span className="text-gray-500">Estado civil</span>
                                  <span>{p.civilStatus || '-'}</span>
                                </div>
                              </section>

                              <section className="space-y-1">
                                <h4 className="text-sm font-semibold text-gray-800">Seguridad social</h4>
                                <div className="grid grid-cols-2 gap-x-4 text-sm">
                                  <span className="text-gray-500">EPS</span>
                                  <span>{p.eps || '-'}</span>
                                  <span className="text-gray-500">Fondo de Pensiones</span>
                                  <span>{p.pensionFund || '-'}</span>
                                  <span className="text-gray-500">AFP</span>
                                  <span>{p.afp || '-'}</span>
                                  <span className="text-gray-500">Riesgo ARL %</span>
                                  <span>{p.arlRiskPercent || '-'}</span>
                                </div>
                              </section>

                              <section className="space-y-1">
                                <h4 className="text-sm font-semibold text-gray-800">Contrato</h4>
                                <div className="grid grid-cols-2 gap-x-4 text-sm">
                                  <span className="text-gray-500">Fecha contrato</span>
                                  <span>{p.contractDate || '-'}</span>
                                  <span className="text-gray-500">Número de contrato</span>
                                  <span>{p.contractNumber || '-'}</span>
                                  <span className="text-gray-500">Modalidad contrato actual</span>
                                  <span>{p.currentContractModality || '-'}</span>
                                  <span className="text-gray-500">Fecha otrosí</span>
                                  <span>
                                    {Array.isArray(p.otrosiDates) && p.otrosiDates.length > 0 ? (
                                      <div className="flex flex-col">
                                        {p.otrosiDates.filter(Boolean).map((d, idx) => (
                                          <span key={`${d}-${idx}`}>{d}</span>
                                        ))}
                                      </div>
                                    ) : (
                                      p.otrosiDate || '-'
                                    )}
                                  </span>
                                </div>
                              </section>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}
              </div>
            )}

            {selectedPerson && (
              <div className="bg-white border rounded-lg p-4 text-gray-800">
                <div className="flex flex-col sm:flex-row gap-4 items-start">
                  <div className="w-32 h-32 border rounded overflow-hidden bg-gray-50 flex items-center justify-center">
                    {selectedPerson.photo ? (
                      <img src={selectedPerson.photo} alt="Foto de perfil" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-xs text-gray-400">Sin foto</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xl font-semibold truncate">{selectedPerson.name || '-'}</h3>
                    <p className="text-gray-600">{formatCargoRolLine(selectedPerson)}{selectedPerson.area ? ` · ${selectedPerson.area}` : ''}</p>
                    <p className="text-sm text-gray-500">Documento: {selectedPerson.id || '-'}</p>
                    <div className="mt-3 max-w-sm flex flex-col sm:flex-row gap-2 items-stretch sm:items-end">
                      <div className="flex-1">
                        <label className="block text-xs text-gray-600 mb-1">Rol operativo</label>
                        <select
                          value={cvOperationalRole}
                          onChange={(e) => setCvOperationalRole(e.target.value)}
                          className="w-full border rounded-md px-2 py-1.5 text-sm"
                        >
                          <option value="">Sin asignar</option>
                          {operationalRoleCatalog.map((label) => (
                            <option key={`cv-${label}`} value={label}>{label}</option>
                          ))}
                        </select>
                      </div>
                      <button
                        type="button"
                        disabled={cvRoleSaving}
                        onClick={saveCvOperationalRole}
                        className="px-3 py-1.5 rounded-md text-sm border bg-white hover:bg-gray-50 disabled:opacity-60"
                      >
                        {cvRoleSaving ? 'Guardando…' : 'Guardar rol'}
                      </button>
                    </div>
                  </div>
                  <div className="text-sm">
                    <p><span className="font-medium">Estado:</span> {selectedPerson.status || '-'}</p>
                    <p><span className="font-medium">Validación Cliente:</span> {selectedPerson.clientValidation || '-'}</p>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <section className="space-y-1">
                    <h4 className="text-sm font-semibold text-gray-800">Datos personales</h4>
                    <div className="grid grid-cols-2 gap-x-4 text-sm">
                      <span className="text-gray-500">Fecha de nacimiento</span>
                      <span>{selectedPerson.birthDate || '-'}</span>
                      <span className="text-gray-500">Años</span>
                      <span>{computeAge(selectedPerson.birthDate) || '-'}</span>
                      <span className="text-gray-500">Tipo de sangre</span>
                      <span>{selectedPerson.bloodType || '-'}</span>
                      <span className="text-gray-500">Dirección</span>
                      <span>{selectedPerson.address || '-'}</span>
                      <span className="text-gray-500">Fecha de ingreso</span>
                      <span>{selectedPerson.hireDate || '-'}</span>
                    </div>
                  </section>

                  <section className="space-y-1">
                    <h4 className="text-sm font-semibold text-gray-800">Contacto</h4>
                    <div className="grid grid-cols-2 gap-x-4 text-sm">
                      <span className="text-gray-500">Teléfono</span>
                      <span>{selectedPerson.phone || '-'}</span>
                      <span className="text-gray-500">Email</span>
                      <span>{selectedPerson.email || '-'}</span>
                      <span className="text-gray-500">Estado civil</span>
                      <span>{selectedPerson.civilStatus || '-'}</span>
                    </div>
                  </section>

                  <section className="space-y-1">
                    <h4 className="text-sm font-semibold text-gray-800">Seguridad social</h4>
                    <div className="grid grid-cols-2 gap-x-4 text-sm">
                      <span className="text-gray-500">EPS</span>
                      <span>{selectedPerson.eps || '-'}</span>
                      <span className="text-gray-500">Fondo de Pensiones</span>
                      <span>{selectedPerson.pensionFund || '-'}</span>
                      <span className="text-gray-500">AFP</span>
                      <span>{selectedPerson.afp || '-'}</span>
                      <span className="text-gray-500">Riesgo ARL %</span>
                      <span>{selectedPerson.arlRiskPercent || '-'}</span>
                    </div>
                  </section>

                  <section className="space-y-1">
                    <h4 className="text-sm font-semibold text-gray-800">Contrato</h4>
                    <div className="grid grid-cols-2 gap-x-4 text-sm">
                      <span className="text-gray-500">Fecha contrato</span>
                      <span>{selectedPerson.contractDate || '-'}</span>
                      <span className="text-gray-500">Número de contrato</span>
                      <span>{selectedPerson.contractNumber || '-'}</span>
                      <span className="text-gray-500">Modalidad contrato actual</span>
                      <span>{selectedPerson.currentContractModality || '-'}</span>
                      <span className="text-gray-500">Fecha otrosí</span>
                      <span>
                        {Array.isArray(selectedPerson.otrosiDates) && selectedPerson.otrosiDates.length > 0 ? (
                          <div className="flex flex-col">
                            {selectedPerson.otrosiDates.filter(Boolean).map((d, idx) => (
                              <span key={`${d}-${idx}`}>{d}</span>
                            ))}
                          </div>
                        ) : (
                          selectedPerson.otrosiDate || '-'
                        )}
                      </span>
                    </div>
                  </section>

                  <section className="space-y-1">
                    <h4 className="text-sm font-semibold text-gray-800">Salud ocupacional</h4>
                    <div className="grid grid-cols-2 gap-x-4 text-sm">
                      <span className="text-gray-500">EMO</span>
                      <span>{selectedPerson.emoDate || '-'}</span>
                      <span className="text-gray-500">Próximo EMO</span>
                      <span>
                        {selectedPerson.nextEmoDate || '-'}
                        {(() => {
                          const d = daysTo(selectedPerson.nextEmoDate);
                          if (d === null) return null;
                          const base = 'ml-2 px-2 py-0.5 rounded text-xs border';
                          const cls = d < 0
                            ? 'bg-red-100 text-red-700 border-red-200'
                            : d <= 30
                              ? 'bg-yellow-100 text-yellow-800 border-yellow-200'
                              : 'bg-green-100 text-green-700 border-green-200';
                          const txt = d < 0 ? `Vencido ${Math.abs(d)}d` : d <= 30 ? `Próximo ${d}d` : `En ${d}d`;
                          return <span className={`${base} ${cls}`}>{txt}</span>;
                        })()}
                      </span>
                    </div>
                  </section>

                  <section className="md:col-span-2 space-y-1">
                    <h4 className="text-sm font-semibold text-gray-800">Formación</h4>
                    <p className="text-sm whitespace-pre-line">{selectedPerson.education || '-'}</p>
                  </section>

                  <section className="md:col-span-2 space-y-1">
                    <h4 className="text-sm font-semibold text-gray-800">Experiencia laboral</h4>
                    {Array.isArray(selectedPerson.workExperience) && selectedPerson.workExperience.length > 0 ? (
                      <div className="border rounded-md divide-y text-sm">
                        {selectedPerson.workExperience.map((w, idx) => (
                          <div key={idx} className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-0.5 px-3 py-1.5">
                            <span><span className="text-gray-500 sm:hidden">Empresa: </span>{w?.company || '-'}</span>
                            <span><span className="text-gray-500 sm:hidden">Cargo: </span>{w?.position || '-'}</span>
                            <span className="text-gray-600">{w?.startDate || '¿?'} a {w?.endDate || 'Actual'}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">Sin experiencia laboral registrada.</p>
                    )}
                  </section>

                  {selectedPerson.signature && (
                    <section className="md:col-span-2 space-y-1">
                      <h4 className="text-sm font-semibold text-gray-800">Firma</h4>
                      <img src={selectedPerson.signature} alt="Firma" className="h-16 object-contain border rounded bg-white" />
                    </section>
                  )}

                  {availableCertificates.length > 0 && (
                    <section className="md:col-span-2 space-y-1">
                      <h4 className="text-sm font-semibold text-gray-800">Certificados y adjuntos</h4>
                      <p className="text-xs text-gray-500">Marca cuáles se incluyen al imprimir la hoja de vida.</p>
                      <ul className="text-sm divide-y border rounded-md">
                        {availableCertificates.map((c) => (
                          <li key={c.key} className="flex items-center justify-between gap-2 px-3 py-1.5">
                            <label className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedCertKeys.has(c.key)}
                                onChange={() => toggleCertKey(c.key)}
                              />
                              <span className="truncate">{c.label}</span>
                            </label>
                            <a href={c.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs shrink-0">Ver</a>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}

                  <section className="md:col-span-2 space-y-1">
                    <h4 className="text-sm font-semibold text-gray-800">Código QR</h4>
                    {(() => {
                      const qrData = encodeURIComponent(JSON.stringify({
                        id: selectedPerson.id,
                        name: selectedPerson.name,
                        position: selectedPerson.position,
                        operationalRole: selectedPerson.operationalRole,
                        phone: selectedPerson.phone,
                        area: selectedPerson.area,
                      }));
                      const url = `https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${qrData}`;
                      return (
                        <img
                          src={url}
                          alt="QR de verificación"
                          className="border border-black"
                          style={{ width: 120, height: 120, objectFit: 'contain' }}
                        />
                      );
                    })()}
                  </section>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === 'vacaciones' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <h4 className="text-base font-semibold">Control de Vacaciones</h4>
              <button
                type="button"
                onClick={loadVacationRecords}
                disabled={vacationsLoading}
                className="px-3 py-2 rounded-md text-sm border bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              >
                {vacationsLoading ? 'Actualizando...' : 'Actualizar'}
              </button>
            </div>
            <div className="max-w-sm">
              <input
                value={vacationsQuery}
                onChange={(e) => setVacationsQuery(e.target.value)}
                className="w-full border rounded-md px-3 py-2 text-sm bg-gray-50"
                placeholder="Buscar por documento, nombre o cargo"
              />
            </div>
            <div className="overflow-x-auto border rounded-lg">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-100 text-gray-700">
                    <th className="px-4 py-2 text-left">Documento</th>
                    <th className="px-4 py-2 text-left">Nombre</th>
                    <th className="px-4 py-2 text-left">Cargo</th>
                    <th className="px-4 py-2 text-left">Fecha inicio (contratación)</th>
                    <th className="px-4 py-2 text-right">Días tomados</th>
                    <th className="px-4 py-2 text-right">Períodos pendientes</th>
                    <th className="px-4 py-2 text-right">Total (períodos)</th>
                    <th className="px-4 py-2 text-left">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {(filteredVacationPeople || []).map((p) => {
                    const joinDate = p.contractDate || p.hireDate || p.entryDate || p.fechaIngreso || '';
                    const records = getVacationRecordsFor(p);
                    const periodTaken = records.reduce((acc, r) => acc + (Number(r.days_taken) || 0), 0);
                    const accruedDays = computeAccruedDays(joinDate);
                    const totalPeriods = Math.floor(accruedDays / 15);
                    const pendingPeriods = Math.max(Math.floor((Math.max(accruedDays - periodTaken, 0)) / 15), 0);
                    return (
                      <tr key={p.id} className="border-t">
                        <td className="px-4 py-2">{p.id}</td>
                        <td className="px-4 py-2">{p.name}</td>
                        <td className="px-4 py-2">{p.position || '-'}</td>
                        <td className="px-4 py-2">{joinDate || '-'}</td>
                        <td className="px-4 py-2 text-right">{periodTaken}</td>
                        <td className="px-4 py-2 text-right font-semibold">{pendingPeriods}</td>
                        <td className="px-4 py-2 text-right font-semibold">{totalPeriods}</td>
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <button
                              type="button"
                              onClick={() => setVacationFormTarget({ person: p, record: null })}
                              className="px-2.5 py-1 rounded bg-green-600 text-white hover:bg-green-700 text-xs"
                            >
                              Registrar vacación
                            </button>
                            <button
                              type="button"
                              onClick={() => setVacationHistoryTarget(p)}
                              className="px-2.5 py-1 rounded border bg-white hover:bg-gray-50 text-xs"
                            >
                              Ver historial{records.length > 0 ? ` (${records.length})` : ''}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredVacationPeople.length === 0 && (
                    <tr>
                      <td className="px-4 py-3 text-center text-gray-500" colSpan={8}>
                        {activePeople.length === 0 ? 'No hay colaboradores activos' : 'Sin resultados para la búsqueda'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'novedades' && (
          <HrNoveltiesModule location={location} people={activePeople} />
        )}

        {tab === 'indicadores' && (
          <RhIndicadoresModule location={location} />
        )}

        {tab === 'dotaciones' && (
          <DotacionesModule people={activePeople} />
        )}
      </section>

      {vacationFormTarget && (
        <VacationFormModal
          person={vacationFormTarget.person}
          record={vacationFormTarget.record}
          onClose={() => setVacationFormTarget(null)}
          onSaved={async () => {
            setVacationFormTarget(null);
            await loadVacationRecords();
          }}
        />
      )}
      {vacationHistoryTarget && (
        <VacationHistoryModal
          person={vacationHistoryTarget}
          records={getVacationRecordsFor(vacationHistoryTarget)}
          onClose={() => setVacationHistoryTarget(null)}
          onEdit={(record) => {
            setVacationHistoryTarget(null);
            setVacationFormTarget({ person: vacationHistoryTarget, record });
          }}
          onDeleted={loadVacationRecords}
        />
      )}
    </div>
  );
}
